import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  Trash3,
  Telephone,
  Envelope,
  Search,
  PlusLg,
  BoxSeam,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { useNavigate } from "react-router-dom";
import { deleteVendors, getVendorsList } from "../../services/vendorService";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import { useTranslation } from "react-i18next";
import { notify } from "../../components/Common/Alerts";
import config from "../../config";

const VendorList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [params, setParams] = useState({
    limit: config.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
  });
  const [vendorList, setVendorList] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchVendorList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true); // full page loader
    } else {
      setLoadingPage(true); // pagination loader
    }
    getVendorsList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setVendorList((prev) =>
          params.is_reload ? newData : [...prev, ...newData],
        );

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        if (!isMobile) setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchVendorList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setVendorList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleDeleteVendor = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);
    deleteVendors({ uuid: deleteId.uuid })
      .then((response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);
        setVendorList((prev) =>
          prev.filter((item) => item.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
      })
      .catch((err) => notify.error(Utils.getErrorMessage(err)))
      .finally(() => setBtnDeleteLoading(false));
  };

  const handleParams = (page) => setParams((prev) => ({ ...prev, page }));

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 10,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("vendor")}
            </h6>
            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("partnersOnboarded")}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              className="mobile-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
            </button>

            <button
              className="desktop-filter-btn"
              onClick={() => setShowOffcanvas(true)}
            >
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>

            <button className="add-btn" onClick={() => navigate("/vendor-add")}>
              <PlusLg />
              <span className="hidden md:block">{t("addNew")}</span>
            </button>
          </div>
        </div>

        <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
          <div className="overflow-auto flex-1 custom-scrollbar">
            <table className="w-full text-left border-separate border-spacing-0">
              {/* <thead className="bg-brand-orange dark:bg-slate-900 sticky top-0 z-10 border-b border-white/5"> */}
              <thead className="table-header">
                <tr>
                  <th className="table-header-text first:rounded-tl-[40px]">
                    #
                  </th>
                  <th className="table-header-text">{t("vendorName")}</th>
                  <th className="table-header-text">{t("storeName")}</th>
                  <th className="table-header-text">{t("mobile")}</th>
                  <th className="table-header-text">{t("email")}</th>
                  <th className="table-header-text">{t("vendorType")}</th>
                  <th className="table-header-text">{t("status")}</th>
                  <th className="table-header-text text-right last:rounded-tr-[40px]">
                    {t("action")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                {loading ? (
                  <DesktopTableSkeleton
                    rows={vendorList?.length || 5}
                    columns={7}
                  />
                ) : vendorList?.length > 0 ? (
                  vendorList?.map((item, i) => (
                    <tr key={item.uuid} className="border-b">
                      <td className="table-header-body font-bold">
                        {startIndex + i + 1}
                      </td>

                      <td className="table-header-body">{item.vendor_name}</td>
                      <td className="table-header-body">{item.store_name}</td>
                      <td className="table-header-body">{item.mobile}</td>
                      <td className="table-header-body">{item.email}</td>
                      <td className="table-header-body">
                        {item.vendor_type || "-"}
                      </td>
                      <td className="table-header-body">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            item.status === 1
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {item.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="table-header-body">
                        <div className="flex items-center justify-end gap-2">
                          <TooltipWrapper title={t("edit")} placement="top">
                            <button
                              className="edit-btn"
                              onClick={() =>
                                navigate(`/vendor-update/${item.uuid}`)
                              }
                            >
                              <PencilSquare />
                            </button>
                          </TooltipWrapper>
                          <TooltipWrapper title={t("delete")} placement="top">
                            <button
                              className="delete-btn"
                              onClick={() => {
                                setDeleteId(item);
                                setDeleteShow(true);
                              }}
                            >
                              <Trash3 />
                            </button>
                          </TooltipWrapper>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="text-center py-6 text-slate-400 dark:text-slate-500 font-medium"
                    >
                      {t("noVendorFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="md:hidden flex-1 space-y-4 overflow-y-auto pb-4 no-scrollbar"
          onScroll={handleScroll}
          style={{ marginBottom: "88px" }}
        >
          {skeletonLoading ? (
            <FlexibleSkeleton
              count={params.limit}
              showAvatar
              showName
              showRole
              showContact
              showButtons
            />
          ) : vendorList?.length > 0 ? (
            vendorList.map((item) => (
              <div
                key={item.uuid}
                className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[35px] p-5 shadow-sm space-y-4 mx-1 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div>
                    <h4 className="font-black text-slate-800 dark:text-white">
                      {item.vendor_name}
                    </h4>

                    <span className="text-[10px] bg-orange-50 dark:bg-brand-orange/10 text-brand-orange px-2 py-0.5 rounded-md font-bold uppercase tracking-widest">
                      {item.store_name}
                    </span>
                  </div>
                </div>

                <div className="space-y-3 py-3 border-y border-slate-50 dark:border-slate-800">
                  <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm font-bold">
                    <Telephone className="text-brand-orange" /> {item.mobile}
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm font-bold">
                    <Envelope className="text-brand-orange" /> {item.email}
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 text-sm font-bold">
                    <Envelope className="text-brand-orange" />{" "}
                    {item.vendor_type}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  <button
                    onClick={() => navigate(`/vendor-update/${item.uuid}`)}
                    className="edit-mobile-btn"
                  >
                    <PencilSquare size={16} /> {t("edit")}
                  </button>
                  <button
                    onClick={() => {
                      setDeleteId(item);
                      setDeleteShow(true);
                    }}
                    className="delete-mobile-btn"
                  >
                    <Trash3 size={16} /> {t("delete")}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">
              <BoxSeam className="no-data-icon" />
              <h3 className="no-data-title">{t("noVendorFound")}</h3>
              <p className="no-data-text">{t("tryAdjustingFilter")}</p>
              <button onClick={handleReset} className="no-data-btn">
                {t("refresh")}
              </button>
            </div>
          )}
          {loadingPage && (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
            </div>
          )}
        </div>
        {vendorList?.length > 0 && (
          <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
              {t("showing")}{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {startIndex + 1}
              </span>{" "}
              {t("to")}{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                {Math.min(endIndex, totalRecord)}
              </span>{" "}
              {t("of")} {totalRecord}
            </p>

            <div className="flex items-center gap-2">
              {/* Prev Button */}
              <button
                onClick={() => handleParams(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
              >
                <ChevronLeft size={18} />
              </button>

              {/* Desktop-only Page Numbers */}
              <div className="hidden md:flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handleParams(page)}
                      className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                        currentPage === page
                          ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                          : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              {/* Mobile-only Page Indicator */}
              <div className="md:hidden px-4">
                <span className="text-sm font-black text-brand-orange">
                  {t("page")} {currentPage} {t("of")} {totalPages}
                </span>
              </div>

              {/* Next Button */}
              <button
                onClick={() => handleParams(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterVendor")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <div className="flex gap-3 flex-row justify-end w-full">
            <button className="btn-filter-close" onClick={handleReset}>
              {t("reset")}
            </button>

            <button className="btn-submit-orange" onClick={handleSearch}>
              {t("applyFilters")}
            </button>
          </div>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label className="input-label">{t("searchByNameEmail")}</label>
            <input
              type="text"
              className="input-field"
              placeholder="Ex: John Doe"
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={() => setDeleteShow(false)}
        onConfirm={handleDeleteVendor}
        loading={btnDeleteLoading}
        titleKey={t("deleteVendor")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.full_name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={40} className="text-red-500" />}
      />
    </>
  );
};

export default VendorList;
